import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Receipt, CircleCheck, AlertCircle, RefreshCw } from "lucide-react";
import api, { formatErr, API } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";
import { deNumber } from "../lib/numberFormat";

// Feb 2026 · 17 Sep 2026 · Kundenbereich „Meine Rechnungen (sevdesk)".
// User-Directive: Kunden sehen NUR ihre eigenen Rechnungen. PDF-Download
// läuft über `/api/customer/sevdesk-invoices/{id}/pdf` (Kunden-Auth).
// Testdaten (is_test=true) werden mit einem gold-farbenen „Testdaten"-Chip
// gekennzeichnet, damit klar ist, dass es sich nicht um echte Rechnungen
// handelt, solange der Live-sevDesk-Token noch fehlt.
const STATUS_META = {
  paid:            { label: "Bezahlt",     bg: STATUS_COLORS.green, fg: "#fff" },
  open:            { label: "Offen",       bg: BRAND_COLORS.gold,   fg: BRAND_COLORS.navy },
  partially_paid:  { label: "Teilbezahlt", bg: BRAND_COLORS.gold,   fg: BRAND_COLORS.navy },
  cancelled:       { label: "Storniert",   bg: STATUS_COLORS.red,   fg: "#fff" },
  unknown:         { label: "Unbekannt",   bg: "#e5e7eb",           fg: "#374151" },
};

function StatusBadge({ status }) {
  const s = STATUS_META[status] || STATUS_META.unknown;
  return (
    <span
      data-testid={`sevdesk-invoice-status-${status}`}
      className="inline-flex items-center gap-1 px-2.5 py-1 text-[10.5px] tracking-wider uppercase rounded-full shrink-0"
      style={{ background: s.bg, color: s.fg }}
    >
      {status === "paid" && <CircleCheck className="w-3 h-3" strokeWidth={2} />}
      {s.label}
    </span>
  );
}

export default function CustomerSevdeskInvoices() {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/customer/sevdesk-invoices");
      setRows(data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const fmtDate = (iso) => iso ? deDateMedium(iso) : "—";
  const fmtMoney = (n, cur = "EUR") => `${deNumber(n || 0, 2)} ${cur}`;

  const download = async (row) => {
    setBusyId(row.id);
    try {
      const url = `${API}/customer/sevdesk-invoices/${row.id}/pdf`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail || "PDF konnte nicht geladen werden");
      }
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = row.pdf_filename || `rechnung-${row.invoice_number || row.id}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error(err.message || "Download fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 pb-24" data-testid="customer-sevdesk-invoices">
      <div className="flex items-center gap-3 mb-2">
        <Receipt className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
        <h1 className="font-display text-3xl md:text-4xl text-navy">Meine Rechnungen</h1>
      </div>
      <p className="text-navy-dark/70 text-[13.5px] mb-6 leading-relaxed">
        Alle Rechnungen, die wir für Sie über sevDesk verbucht haben. Sie können jede Rechnung als PDF herunterladen.
      </p>

      {rows === null && (
        <div className="text-navy-dark/60 italic text-sm" data-testid="sevdesk-invoices-loading">Wird geladen …</div>
      )}
      {rows !== null && rows.length === 0 && (
        <div className="bg-white border border-navy/10 p-6 text-navy-dark/70 leading-relaxed" data-testid="sevdesk-invoices-empty">
          Noch keine Rechnungen vorhanden. Sobald Ihre erste Rechnung in sevDesk verbucht wird, erscheint sie automatisch hier.
        </div>
      )}
      {rows !== null && rows.length > 0 && (
        <ul className="space-y-2" data-testid="sevdesk-invoices-list">
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={`sevdesk-invoice-row-${r.id}`}
              className="bg-white border border-navy/10 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5 hover:border-gold transition-colors"
              style={{ borderLeft: `4px solid ${r.status === "paid" ? STATUS_COLORS.green : r.status === "cancelled" ? STATUS_COLORS.red : BRAND_COLORS.gold}` }}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-lg text-navy" data-testid={`sevdesk-invoice-number-${r.id}`}>
                    {r.invoice_number || "Rechnung"}
                  </span>
                  <StatusBadge status={r.status} />
                  {r.is_test && (
                    <span
                      data-testid={`sevdesk-invoice-test-badge-${r.id}`}
                      className="text-[9.5px] tracking-widest uppercase px-2 py-0.5"
                      style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
                    >
                      Testdaten
                    </span>
                  )}
                  {r.is_ocr_extracted && (
                    <span
                      data-testid={`sevdesk-invoice-ocr-badge-${r.id}`}
                      title="Text wurde per OCR aus einem Scan/Bild-PDF extrahiert."
                      className="text-[9.5px] tracking-widest uppercase px-2 py-0.5"
                      style={{ background: withAlpha(STATUS_COLORS.red, 0.10), color: STATUS_COLORS.red }}
                    >
                      OCR
                    </span>
                  )}
                </div>
                <div className="text-[12.5px] text-navy-dark/70 mt-1 tabular-nums">
                  Datum: {fmtDate(r.invoice_date)}
                  {r.sevdesk_customer_number && (
                    <span data-testid={`sevdesk-invoice-cust-num-${r.id}`} className="ml-2">
                      · Kd-Nr. <b>{r.sevdesk_customer_number}</b>
                    </span>
                  )}
                </div>
              </div>
              <div className="tabular-nums text-[15px] text-navy font-medium" data-testid={`sevdesk-invoice-amount-${r.id}`}>
                {fmtMoney(r.sum_gross, r.currency)}
              </div>
              <button
                type="button"
                onClick={() => download(r)}
                disabled={busyId === r.id}
                data-testid={`sevdesk-invoice-pdf-btn-${r.id}`}
                className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-navy px-4 py-2 border border-navy/25 hover:border-gold hover:bg-gold/5 transition-colors disabled:opacity-50"
              >
                {busyId === r.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} /> : <FileDown className="w-3.5 h-3.5" strokeWidth={1.8} />}
                PDF
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
